import { captureException } from "@sentry/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useNotificationMarksAsReadMutation } from "web_myplann/Api/Notifications/useNotificationMarksAsReadMutation";
import {
	getQuestionnaireAccessRequest,
	openQuestionnaire,
} from "web_myplann/Api/Nsure/openQuestionnaire";
import { useQuestionnaireAccessMutation } from "web_myplann/Api/Nsure/useQuestionnaireAccessMutation";
import {
	NotificationType,
	useGetIconByType,
} from "web_myplann/Dashboard/Hooks/useGetIconByType";
import {
	type NotificationListItemResponseDTO,
	NotificationTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { ApiCallError } from "web_myplann/Infrastructure/Api/ApiUtils";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { device, NavigationTypes } from "web_myplann/Utils/device";

export const useNotificationClick = ({
	type,
	id,
	sourceId,
	customContent,
}: Pick<
	NotificationListItemResponseDTO,
	"type" | "id" | "sourceId" | "customContent"
>) => {
	const navigate = useNavigate();
	const { t } = useResource();

	const [isLoading, setIsLoading] = useState(false);

	const { type: notificationType } = useGetIconByType(type, "");

	const { mutate: markAsRead } = useNotificationMarksAsReadMutation();
	const { mutateAsync: getQuestionnaireAccess } =
		useQuestionnaireAccessMutation();

	const isGroupedNotification =
		type === NotificationTypeEnum.ActivityGroup ||
		type === NotificationTypeEnum.TaskGroup;

	const onMarkAsReadError = (error: Error) => {
		captureException(error, (scope) => {
			scope.setExtra("notification_id", id);
			scope.setExtra("notification_type", type);
			return scope;
		});
	};

	const markGroupedAsRead = () => {
		if (Array.isArray(customContent?.notificationIds)) {
			for (const notificationId of customContent.notificationIds) {
				markAsRead(notificationId as number, { onError: onMarkAsReadError });
			}
		}
	};

	const handleClick = async () => {
		setIsLoading(true);
		try {
			if (isGroupedNotification) {
				markGroupedAsRead();
			} else {
				markAsRead(id, { onError: onMarkAsReadError });
			}

			if (notificationType === NotificationType.meetingRecord) {
				if (customContent?.url && typeof customContent.url === "string") {
					await device.navigationRequest(
						NavigationTypes.ZZJ,
						customContent?.url,
					);
				}
				return;
			}
			if (notificationType === NotificationType.investmentQuestionnaire) {
				const requestContract = getQuestionnaireAccessRequest({
					notificationId: id,
					clientType: customContent?.clientType,
					identificationNumber: customContent?.identificationNumber,
				});

				if (!requestContract) {
					return;
				}

				const access = await getQuestionnaireAccess(requestContract);

				if (!(await openQuestionnaire(access))) {
					toast.error(t(Resources.Common.somethingWentWrong));
				}
				return;
			}
			if (!sourceId) {
				logWarning("Notification click without sourceId", {
					notification_id: id,
					notification_type: type,
				});
				toast.error(t(Resources.Common.somethingWentWrong));
				return;
			}

			if (notificationType === NotificationType.task) {
				navigate(Routing.TaskDetail.path(sourceId));
			} else {
				navigate(Routing.ActivityDetail.path(sourceId));
			}
		} catch (error) {
			if (!(error instanceof ApiCallError)) {
				captureException(error);
				toast.error(t(Resources.Common.somethingWentWrong));
			}
		} finally {
			setIsLoading(false);
		}
	};

	return { handleClick, isLoading };
};
